import { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Deferred Query Invalidation Manager — copied from mDenials-portal.
 *
 * Solves two problems:
 * 1. Background worker APIs — immediate invalidation cannot get the latest result.
 * 2. Multiple API calls in a Dialog — each invalidation causes re-render/flicker.
 *
 * Solution:
 * - Debounced execution: collect all invalidation requests within a time window, then batch execute.
 * - Deduplication: same query key only invalidates once.
 */

const DEFAULT_DELAY = 300;

const pendingInvalidations = new Map<string, QueryKey>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let queryClientRef: QueryClient | null = null;

function queryKeyToString(queryKey: QueryKey): string {
    return JSON.stringify(queryKey);
}

async function flushInvalidations(): Promise<void> {
    if (!queryClientRef || pendingInvalidations.size === 0) {
        return;
    }
    const keysToInvalidate = Array.from(pendingInvalidations.values());
    pendingInvalidations.clear();
    await Promise.all(
        keysToInvalidate.map((queryKey) => queryClientRef!.invalidateQueries({ queryKey })),
    );
}

function isQueryKeyArray(keys: QueryKey | QueryKey[]): keys is QueryKey[] {
    if (!Array.isArray(keys)) return false;
    return Array.isArray(keys[0]);
}

export function deferredInvalidateQueries(
    queryClient: QueryClient,
    queryKeys: QueryKey | QueryKey[],
    delay: number = DEFAULT_DELAY,
): void {
    queryClientRef = queryClient;
    const keysArray = isQueryKeyArray(queryKeys) ? queryKeys : [queryKeys];
    keysArray.forEach((queryKey) => {
        pendingInvalidations.set(queryKeyToString(queryKey), queryKey);
    });
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        flushInvalidations();
    }, delay);
}

export function deferredInvalidateAllQueries(
    queryClient: QueryClient,
    delay: number = DEFAULT_DELAY,
): void {
    queryClientRef = queryClient;
    pendingInvalidations.clear();
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        queryClientRef?.invalidateQueries();
    }, delay);
}

export async function flushDeferredInvalidations(): Promise<void> {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
    await flushInvalidations();
}
